import { useMemo } from 'react';
import type {
  AuditLogEntry,
  AuditLogFilters,
  AuditLogResponse,
} from '@/api/audit';
import { listAuditLogs } from '@/api/audit';
import { mockAuditLogs } from '@/data/mock-audit-data';
import { useApiQuery } from './useApiQuery';

const getDateFromTimestamp = (date: string, isEndOfDay: boolean): number => {
  const timestamp = new Date(`${date}T00:00:00`);
  if (isEndOfDay) {
    timestamp.setHours(23, 59, 59, 999);
  }
  return timestamp.getTime();
};

const filterMockAuditLogs = (
  filters: AuditLogFilters,
  page: number,
  limit: number,
): AuditLogResponse => {
  const filteredLogs = mockAuditLogs.filter((log) => {
    if (filters.action && log.action !== filters.action) {
      return false;
    }

    if (filters.entityType && log.entityType !== filters.entityType) {
      return false;
    }

    if (
      filters.actor &&
      !log.actor.toLowerCase().includes(filters.actor.toLowerCase())
    ) {
      return false;
    }

    const createdAtTimestamp = new Date(log.createdAt).getTime();

    if (
      filters.dateFrom &&
      createdAtTimestamp < getDateFromTimestamp(filters.dateFrom, false)
    ) {
      return false;
    }

    if (
      filters.dateTo &&
      createdAtTimestamp > getDateFromTimestamp(filters.dateTo, true)
    ) {
      return false;
    }

    return true;
  });

  const pageStartIndex = (page - 1) * limit;
  const pagedLogs = filteredLogs.slice(pageStartIndex, pageStartIndex + limit);

  return {
    logs: pagedLogs,
    total: filteredLogs.length,
  };
};

interface UseAuditLogsOptions {
  filters: AuditLogFilters;
  page: number;
  limit: number;
}

interface UseAuditLogsResult {
  logs: AuditLogEntry[];
  total: number;
  isLoading: boolean;
  error: string | undefined;
  isUsingMockData: boolean;
  reload: () => void;
}

export const useAuditLogs = (options: UseAuditLogsOptions): UseAuditLogsResult => {
  const { filters, page, limit } = options;

  const mockResponse = useMemo(
    () =>
      import.meta.env.DEV
        ? filterMockAuditLogs(filters, page, limit)
        : undefined,
    [filters, page, limit],
  );

  const query = useApiQuery<AuditLogResponse>(
    {
      queryFn: () =>
        listAuditLogs({
          ...filters,
          page,
          limit,
        }),
      mockData: mockResponse,
    },
    [filters, page, limit],
  );

  return {
    logs: query.data?.logs ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    isUsingMockData: query.isUsingMockData,
    reload: query.reload,
  };
};
